import { Card } from "@general-backoffice/core";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, ButtonGroup, Card as RSCard, CardBody } from "reactstrap";

import getCurrencySymbol from "../../utils/getCurrencySymbol";
import useTimeSelection from "../hooks/selection/useTimeSelection";
import { LegalFooter } from "../legal";
import ExtendedIcon from "../shared/ExtendedIcon";
import BotPerformanceGraph from "./BotPerformanceGraph";
import { sliceToPeriod } from "./sliceToPeriod";

interface PerformanceDataPoint {
  timestamp: number;
  absoluteProfit?: number | null;
  drawdown?: number | null;
  profit?: number | null;
}

interface BotPerformanceGraphCardProps {
  isLoading?: boolean;
  data?: PerformanceDataPoint[];
  currency?: string;
  stopLoss?: number;
  sailingMode?: number;
}

const BotPerformanceGraphCard = ({
  isLoading,
  data: performanceHistory = [],
  currency,
  stopLoss,
  sailingMode,
}: BotPerformanceGraphCardProps) => {
  const { t } = useTranslation("stats", { keyPrefix: "performanceGraphCard" });

  const { options: timeOptions, selectedOption: timeSelectedOption } = useTimeSelection();

  const {
    points: periodPerformanceHistory,
    first: firstData,
    last: lastData,
  } = useMemo(
    () => sliceToPeriod(performanceHistory, timeSelectedOption?.timestamp),
    [performanceHistory, timeSelectedOption?.timestamp]
  );

  const periodProfit = (lastData?.absoluteProfit || 0) - (firstData?.absoluteProfit || 0);

  const showDrawdown = !!performanceHistory.find((d) => d?.drawdown !== undefined);
  const periodDrawdown = (lastData?.drawdown || 0) - (firstData?.drawdown || 0);

  const symbol = useMemo(() => getCurrencySymbol(currency || "") || "-", [currency]);
  return (
    <RSCard>
      <Card.Header title={t("title")}>
        <ButtonGroup>
          {timeOptions.map(({ isSelected, select, label, ...rest }, index) => (
            <Button key={index} size="sm" outline color="default" active={isSelected} onClick={select}>
              {rest.isExtended && <ExtendedIcon className="mr-1" />}
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </Card.Header>
      <CardBody className="p-0 pt-4 pb-1">
        <div className="position-relative w-100 mb-4">
          <div
            className="position-absolute d-flex justify-content-around top-0 w-100"
            style={{ transform: "translateY(-50%)" }}
          >
            <Badge
              hidden={!showDrawdown}
              color="default"
              children={`${t("drawdown")}: ${periodDrawdown.toFixed(2) || "---"}`}
            />
            <Badge color="primary" children={`${t("profit", { symbol })}: ${periodProfit.toFixed(2) || "---"}`} />
          </div>
        </div>

        <BotPerformanceGraph
          {...{ stopLoss, sailingMode }}
          data={periodPerformanceHistory}
          currency={currency}
          isLoading={isLoading}
          className="rounded-bottom overflow-hidden"
          style={{ height: "350px" }}
        />

        <div className="px-3 pb-2">
          <LegalFooter type="performance" />
        </div>
      </CardBody>
    </RSCard>
  );
};

export default BotPerformanceGraphCard;
